import { useCallback } from "react";
import { primaryArtifact, isPreviewable, useArtifactStore, viewOf } from "../../../../stores/artifact";
import { type DockPanel, useDockStore } from "../../../../stores/dock";
import { useChatStore } from "../../../../stores/chat";
import { artifactData, artifactPanel } from "./data";

/**
 * Artifact versioning. When the open artifact belongs to the LAST assistant
 * reply and that reply has regenerate siblings, the panel offers a ‹n/m›
 * switcher that flips artifact versions by switching the underlying message
 * branch — the exact machinery as the in-bubble reply switcher, kept in
 * lock-step with it. Gated to the last reply because the server only switches
 * the trailing turn.
 *
 * Carried over from ArtifactCanvas unchanged in behaviour; only the destination
 * moved (the artifact store's `replaceActive` became the dock's `replacePanel`).
 */
export function useArtifactVersions(panel: DockPanel) {
	const messages = useChatStore((s) => s.messages);
	const switchActiveBranch = useChatStore((s) => s.switchActiveBranch);
	const isStreaming = useChatStore((s) => s.isStreaming);
	const replacePanel = useDockStore((s) => s.replacePanel);
	const setView = useArtifactStore((s) => s.setView);

	const { language, content } = artifactData(panel);

	// The reply this artifact came from = the last finalized assistant message
	// whose content contains it. Matching by CONTENT (not id) is what survives
	// the id change a branch switch causes.
	const sourceReply = [...messages]
		.reverse()
		.find((m) => m.role === "assistant" && !m.streaming && m.content.includes(content));
	const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant" && !m.streaming);
	const isLastReply = !!sourceReply && sourceReply.id === lastAssistant?.id;

	const navigateVersion = useCallback(
		async (targetIndex: number) => {
			if (!sourceReply || isStreaming) return;
			await switchActiveBranch(sourceReply.id, targetIndex);
			// Surface the newly-active sibling's artifact so the panel stays in
			// lock-step with the chat. It REPLACES this tab rather than opening one:
			// same artifact, different draft. A tab per version flip would litter the
			// strip with drafts the user is stepping through, not collecting.
			const msgs = useChatStore.getState().messages;
			const newActive = [...msgs].reverse().find((m) => m.role === "assistant" && !m.streaming);
			const next = newActive ? primaryArtifact(newActive.content) : null;
			if (!next) return;
			// Carry the mode you were reading in across the flip — unless the new
			// draft can't be previewed, which would strand it on a blank Preview.
			// The view is keyed by artifact id and the new draft has a new hash, so
			// without this it would silently fall back to the language default.
			const prevView = viewOf(useArtifactStore.getState(), panel.id, language);
			if (isPreviewable(next.language)) setView(next.id, prevView);
			replacePanel(panel.id, artifactPanel(next));
		},
		[sourceReply, isStreaming, switchActiveBranch, replacePanel, setView, panel.id, language],
	);

	return {
		versionCount: isLastReply ? sourceReply.branchCount ?? 1 : 1,
		versionIndex: isLastReply ? sourceReply.branchIndex ?? 0 : 0,
		navigateVersion,
	};
}
